// apps/patient/src/config/navigationConfig.ts

import {
  Home, Calendar, FileText, Heart, MessageSquare,
  Dumbbell, Clock, User, Activity, ClipboardList,
  Menu, CreditCard, TrendingUp, Plus, Users
} from 'lucide-react';

import type { PatientFeatures } from '@prism-ehr/shared';

export interface NavItem {
  path: string;
  label: string;
  icon: any;
  feature?: keyof PatientFeatures['enabled_features'];
  showInTabBar: boolean;
  badge?: number;
  children?: NavItem[];
}

// Get navigation items based on enabled features
export function getNavigationItems(
  features: PatientFeatures['enabled_features'], 
  counts: any = {},
  platform: string = 'ehr'
) {
  const items: NavItem[] = [
    {
      path: '/dashboard',
      label: 'Home',
      icon: Home,
      showInTabBar: true
    },
    {
      path: '/calendar',
      label: 'Calendar',
      icon: Calendar,
      showInTabBar: platform === 'exercise' // Show in tab bar for exercise platform
    }
  ];

  // Health section - only for non-exercise or provider-linked
  if (features.clinical_records) {
    items.push({
      path: '/records',
      label: 'Health Records',
      icon: FileText,
      feature: 'clinical_records',
      showInTabBar: false
    });
  }

  if (features.prescriptions) {
    items.push({
      path: '/prescriptions',
      label: 'Prescriptions',
      icon: Heart,
      feature: 'prescriptions',
      showInTabBar: false
    });
  }

  // Programs / Exercises - show for exercise platform, assigned programs, or standalone exercises
  const hasPrograms = features.rehab_programs || features.workout_plans || features.self_guided_workouts;
  if (hasPrograms || features.standalone_exercises) {
    const exercisesOnly = features.standalone_exercises && !hasPrograms;
    items.push({
      path: '/programs',
      label: exercisesOnly ? 'Exercises' :
        platform === 'exercise' ? 'Programs' : 'Exercise Programs',
      icon: Dumbbell,
      showInTabBar: true,
      badge: counts.activePrograms > 0 ? counts.activePrograms : undefined
    });
  }

  // Progress tracking - show for exercise/rehab users
  // Available to users with workout_plans or rehab_programs
  // Only shows in tab bar for exercise platform (not full EHR)
  const hasExerciseFeatures = features.workout_plans || features.rehab_programs;
  if (hasExerciseFeatures) {
    items.push({
      path: '/progress',
      label: 'Progress',
      icon: TrendingUp,
      showInTabBar: platform === 'exercise'
    });
  }

  // Appointments - only for provider-linked non-exercise
  if (features.appointments) {
    items.push({
      path: '/appointments',
      label: 'Appointments',
      icon: Clock,
      feature: 'appointments',
      showInTabBar: true,
      badge: counts.upcomingAppointments > 0 ? counts.upcomingAppointments : undefined
    });
  }

  // Classes
  if (features.classes) {
    items.push({
      path: '/classes',
      label: 'Browse Classes',
      icon: Users,
      feature: 'classes',
      showInTabBar: false
    });
    items.push({
      path: '/my-classes',
      label: 'My Classes',
      icon: Users,
      feature: 'classes',
      showInTabBar: true
      // TODO: badge: counts.enrolledClasses > 0 ? counts.enrolledClasses : undefined
    });
  }

  // Messages - for provider-linked users
  if (features.messaging) {
    items.push({
      path: '/messages',
      label: 'Messages',
      icon: MessageSquare,
      feature: 'messaging',
      showInTabBar: true,
      badge: counts.unreadMessages > 0 ? counts.unreadMessages : undefined
    });
  }

  // Forms
  if (features.forms) {
    items.push({
      path: '/forms',
      label: 'Forms',
      icon: ClipboardList,
      feature: 'forms',
      showInTabBar: false
    });
  }

  // Billing (clinic invoices). The exercise platform manages its subscription from Profile instead.
  if (platform !== 'exercise') {
    items.push({
      path: '/billing',
      label: 'Billing',
      icon: CreditCard,
      showInTabBar: false,
      badge: counts.unpaidInvoices > 0 ? counts.unpaidInvoices : undefined
    });
  }

  // Profile
  items.push({
    path: '/profile',
    label: 'Profile',
    icon: User,
    showInTabBar: false
  });

  return items;
}

// Get items for mobile bottom tab bar
export function getTabBarItems(items: NavItem[]): NavItem[] {
  return items.filter(item => item.showInTabBar);
}

// Group items for sidebar display
export function getSidebarSections(items: NavItem[], platform: string = 'ehr'): SidebarSection[] {
  const sections: SidebarSection[] = [
    {
      label: 'Main',
      items: items.filter(item => 
        ['/', '/dashboard', '/calendar'].includes(item.path)
      )
    }
  ];

  // For exercise platform, focus on workout-related sections
  if (platform === 'exercise') {
    const workoutItems = items.filter(item => 
      ['/programs', '/progress'].includes(item.path)
    );
    if (workoutItems.length > 0) {
      sections.push({
        label: 'Training',
        items: workoutItems
      });
    }
  } else {
    // Standard health section for EHR/other platforms
    const healthItems = items.filter(item => 
      ['/records', '/prescriptions', '/appointments'].includes(item.path)
    );
    if (healthItems.length > 0) {
      sections.push({
        label: 'Health',
        items: healthItems
      });
    }

    const programItems = items.filter(item => 
      ['/programs', '/progress'].includes(item.path)
    );
    if (programItems.length > 0) {
      sections.push({
        label: 'Programs',
        items: programItems
      });
    }
  }

  const classesItems = items.filter(item =>
    ['/classes', '/my-classes'].includes(item.path)
  );
  if (classesItems.length > 0) {
    sections.push({
      label: 'Classes',
      items: classesItems
    });
  }

  const communicationItems = items.filter(item =>
    ['/messages', '/forms'].includes(item.path)
  );
  if (communicationItems.length > 0) {
    sections.push({
      label: 'Communication',
      items: communicationItems
    });
  }

  const accountItems = items.filter(item => 
    ['/profile', '/billing', '/subscription', '/settings'].includes(item.path)
  );
  if (accountItems.length > 0) {
    sections.push({
      label: 'Account',
      items: accountItems
    });
  }

  return sections;
}

interface SidebarSection {
  label: string;
  items: NavItem[];
}